<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CreateTemplate',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const formId = ref(null);
  const formName = ref('template');
  const item = ref(null);
  const objectTypes = ref(null);
  const templateTypes = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    formName: formName.value,
    item: item.value,
    objectTypes: objectTypes.value,
    templateTypes: templateTypes.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = async () => {
    return $fn.value.schemaDefaults('CreateTemplate');
  };
  const $$load_objectTypes = async () => {
    return $fn.value.getEnumValuesFromSchema('ObjectTypes');
  };
  const $$load_templateTypes = async () => {
    return $fn.value.getEnumValuesFromSchema('TemplateTypes');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'formName' },
      { name: 'item' },
      { name: 'objectTypes' },
      { name: 'templateTypes' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      formId: $$load_formId,
      item: $$load_item,
      objectTypes: $$load_objectTypes,
      templateTypes: $$load_templateTypes,
    },
    ux: {},
    dataRefs: {
      formId,
      formName,
      item,
      objectTypes,
      templateTypes,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.pgettext('Templates', 'Create Template') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_ocxep25cn_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_n8n76q3t6_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        value: $event.data.response.data,
      },
    };
  };
  const inst_evh_1040167445267876_success_f8xrrj3zl_getWhen = async (
    $event
  ) => {
    return !!props.modalName;
  };
  const act_1040167445267876_request_z69pzm0ts_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'create_template',
      data: { ...item.value, content: '<div></div>' },
    };
  };

  const act_7315092382398562_closeModal_ocxep25cn =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_ocxep25cn_getActionArgs,
    });
  const inst_evh_7315092382398562_close_ey2m6vkau =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_ocxep25cn,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_ey2m6vkau],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_6b8pvc7sq =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_0msagjwhf =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_6b8pvc7sq,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_0msagjwhf],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_ij6n79zbt =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_dxbu2cq43 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_ij6n79zbt,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_dxbu2cq43],
    event: 'close',
  });
  const act_1040167445267876_closeModal_n8n76q3t6 =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_n8n76q3t6_getActionArgs,
    });
  const inst_evh_1040167445267876_success_f8xrrj3zl =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_n8n76q3t6,
      event: 'success',
      when: inst_evh_1040167445267876_success_f8xrrj3zl_getWhen,
      displayName: 'closeModal',
    });
  const act_1040167445267876_request_z69pzm0ts = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_z69pzm0ts_getActionArgs,
    displayName: 'submit',
    events: [inst_evh_1040167445267876_success_f8xrrj3zl],
  });
  const inst_evh_1040167445267876_submit_9dafyblk8 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_z69pzm0ts,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_9dafyblk8],
    event: 'submit',
  });
  const act_2434514588044777_closeModal_fvq9rb9x8 =
    new actionTypes.CloseModalAction({});
  const inst_evh_2434514588044777_clickPreventStop_x79t6xyeu =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeModal_fvq9rb9x8,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_x79t6xyeu],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_ey2m6vkau,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_0msagjwhf,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_dxbu2cq43,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_f8xrrj3zl,
    inst_evh_1040167445267876_submit_9dafyblk8,
    submit: inst_evh_1040167445267876_submit_9dafyblk8,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_x79t6xyeu,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_ey2m6vkau = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_ey2m6vkau.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_0msagjwhf = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_0msagjwhf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_dxbu2cq43 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_dxbu2cq43.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_f8xrrj3zl = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_f8xrrj3zl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_9dafyblk8 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_9dafyblk8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_x79t6xyeu = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_x79t6xyeu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CreateTemplate"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :name="formName"
        schema="CreateTemplate"
        :id="formId"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup name="name" :label="pgettext('Templates', 'Name')">
          <ItpFormInput
            name="name"
            size="md"
            v-model="item.name"
            :value="item.name"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.name =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            required
            autofocus
            :placeholder="pgettext('Templates', 'Name of template')"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup name="name" :label="pgettext('Templates', 'Object type')">
          <ItpFormSelect2
            name="name"
            size="md"
            v-model="item.object_type"
            :value="item.object_type"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.object_type =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :options="objectTypes"
            required
          >
          </ItpFormSelect2>
        </ItpFormGroup>
        <ItpFormGroup
          name="template_type"
          :label="pgettext('Templates', 'Template type')"
          :description="
            pgettext(
              'Templates',
              'Emails are generated from MJML templates, PDF documents are generated from HTML templates.'
            )
          "
        >
          <ItpFormSelect2
            name="template_type"
            size="md"
            v-model="item.template_type"
            :value="item.template_type"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.template_type =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            required
            :options="templateTypes"
          >
          </ItpFormSelect2>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :form="formId"
          :text="pgettext('Templates', 'Save')"
          type="submit"
          variant="primary"
          default
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('Templates', 'Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
